<script setup lang="ts">
	import { ref, watch, computed, onBeforeUpdate, onUnmounted } from "vue";
	import { useDisplay } from "vuetify";
	import type { RunAgentOrchestrator } from "../composables/useRunAgentOrchestrator";
	import type { ScheduledPackInputOrchestrator } from "../composables/useScheduledPackInput";
	import type { RunAgentDialogMode } from "../composables/useRunAgentSteps";
	import UserInputFormRenderer from "./UserInputFormRenderer.vue";
	import { provideAutocompleteCommitBarrier } from "./autocompleteCommitBarrier";
	import { isValidatableForm, revealInvalidUserInput, type ValidatableForm } from "./revealInvalidUserInput";
	import { useRunAgentSteps, type Step } from "../composables/useRunAgentSteps";
	import Dialog from "@/components/Dialog.vue";
	import FieldLabel from "@common-ui/components/FieldLabel.vue";

	const display = useDisplay();

	const props = withDefaults(
		defineProps<{
			modelValue: boolean;
			orchestrator: RunAgentOrchestrator | ScheduledPackInputOrchestrator;
			mode?: RunAgentDialogMode;
		}>(),
		{
			mode: "run"
		}
	);

	const emit = defineEmits<{
		"update:modelValue": [value: boolean];
		"analysis-created": [analysisId: string];
		"input-collected": [];
	}>();

	const { selectedPack, runningAnalysis, error, pendingMetadata, reportNameDraft, platformComposable, userInputComposable } =
		props.orchestrator;

	const {
		platformRequirements,
		platformBusy,
		platformErrors,
		platformLoginPending,
		cancelPlatformLogin,
		hasPlatformRequirements,
		allPlatformsAuthenticated,
		loginPlatform,
		logoutPlatform,
		getPlatformAuthIcon,
		getPlatformAuthColor,
		getPlatformAuthLabel
	} = platformComposable;

	const {
		userInputRequirements,
		userInputTaskConfigs,
		userInputData,
		loadingUserInputRequirements,
		hasUserInputItems,
		allUserInputValid,
		missingRequiredFieldLabels,
		invalidFieldLabels,
		handleUserInputUpdate
	} = userInputComposable;

	const modeRef = computed(() => props.mode);

	const stepsComposable = useRunAgentSteps({
		hasPlatformRequirements,
		hasUserInputItems,
		allPlatformsAuthenticated,
		allUserInputValid,
		selectedPack,
		runningAnalysis,
		mode: modeRef
	});
	const { currentStep, stepperSteps, currentStepIndex, buttonText, canProceedFromCurrentStep } = stepsComposable;

	// The v-forms wrapping each renderer, collected through a function ref so a refused Run can make them
	// paint their rules (LSP-19461). Vue re-invokes a function ref on every patch, so the set is rebuilt
	// per render — the documented pattern for refs inside v-for — and an unmounting form arrives as null.
	const userInputForms = new Set<ValidatableForm>();
	const trackUserInputForm = (form: unknown) => {
		if (isValidatableForm(form)) userInputForms.add(form);
	};
	onBeforeUpdate(() => userInputForms.clear());
	const provideDetailsSection = ref<HTMLElement | null>(null);

	// The footer NAMES what is holding the run up (LSP-19461). "Complete required fields" told the user
	// nothing about which field or why; the gate already knows both, so the footer says so.
	const userInputStatusText = computed(() => {
		if (allUserInputValid.value) return "Ready to run";
		const parts: string[] = [];
		if (missingRequiredFieldLabels.value.length > 0) parts.push(`Missing: ${missingRequiredFieldLabels.value.join(", ")}`);
		if (invalidFieldLabels.value.length > 0) parts.push(`Check: ${invalidFieldLabels.value.join(", ")}`);
		return parts.length > 0 ? parts.join(" · ") : "Complete required fields";
	});

	const showLogoutConfirm = ref(false);
	const pendingLogoutCodeName = ref<string | null>(null);
	const pendingLogoutPlatformName = computed(
		() => platformRequirements.value.find((p) => p.codeName === pendingLogoutCodeName.value)?.productName ?? "this platform"
	);

	const requestLogout = (codeName: string) => {
		pendingLogoutCodeName.value = codeName;
		showLogoutConfirm.value = true;
	};

	const confirmLogout = () => {
		if (pendingLogoutCodeName.value) {
			logoutPlatform(pendingLogoutCodeName.value);
		}
		showLogoutConfirm.value = false;
		pendingLogoutCodeName.value = null;
	};

	const cancelLogout = () => {
		showLogoutConfirm.value = false;
		pendingLogoutCodeName.value = null;
	};

	const goBack = () => {
		if (currentStep.value === "provide-details") {
			if (hasPlatformRequirements.value) {
				currentStep.value = "review-requirements";
			} else {
				// At first visible step — close dialog
				emit("update:modelValue", false);
			}
		} else if (currentStep.value === "review-requirements") {
			// At first visible step — close dialog
			emit("update:modelValue", false);
		}
	};

	const navigateToStep = (step: Step) => {
		const targetIdx = stepperSteps.value.findIndex((s) => s.key === step);
		const currentIdx = currentStepIndex.value;
		if (targetIdx < currentIdx) {
			currentStep.value = step;
		}
	};

	// WHICH step is proceeding, not merely THAT one is — the re-entry guard for the flush window below,
	// NOT a validity gate. The flush awaits a real MCP round trip and the button stays enabled
	// throughout, so without this a second click in that window queues a second run.
	//
	// Step-scoped because Back stays clickable during the flush BY DESIGN (see stepAtProceed below), and
	// as a bare boolean this left the earlier step's footer action spun and unclickable until the drain
	// finished: one flag was being read as "this button is busy" when it only ever meant "some step's
	// proceed is in flight". The handler's own StepChangedDuringFlush refusal already covered the other
	// half — not walking a user who backed out forward again — but nothing covered the button state.
	const proceedingStep = ref<Step | null>(null);
	// The guard stays GLOBAL (one flush at a time) while the button state is step-scoped, so backing out
	// re-enables the action without allowing a second concurrent drain.
	const proceeding = computed(() => proceedingStep.value !== null && proceedingStep.value === currentStep.value);

	/** User-visible reason the proceed action refused, so a blocked Run click is never silent. */
	const platformLoginGuidance = "Use Login or Retry login next to each platform to continue. If a login window is stuck, choose Cancel login first.";
	const proceedBlockedMessage = ref("");
	watch(allPlatformsAuthenticated, (authenticated) => {
		if (authenticated && proceedBlockedMessage.value === platformLoginGuidance) {
			proceedBlockedMessage.value = "";
		}
	});
	// WHAT THE USER ACTUALLY SEES, and why it is not all one ref. `proceedBlockedMessage` LATCHES a
	// refusal: it stays up until something replaces or clears it. "another step's proceed is still
	// draining" is not that kind of fact — it is true only while that drain runs. Latching it left the
	// form saying "still finishing" after the wait was already over, because the drain can end via
	// StepChangedDuringFlush, which writes no message of its own and so had nothing to clear. Derived
	// here instead, so it takes itself down, and it wins while it is true because a live wait is more
	// use to the reader than an older latched refusal.
	const proceedWaitingOnOtherStep = computed(() => proceedingStep.value !== null && proceedingStep.value !== currentStep.value);
	const proceedNotice = computed(() =>
		proceedWaitingOnOtherStep.value
			? "We are still finishing the previous step. Give it a moment and try again."
			: proceedBlockedMessage.value
	);

	// This dialog OWNS its barrier and shares it with every renderer below, so clearing it can only ever
	// affect this dialog's own commits. It used to be module-wide, which made this line reach into any
	// other resident RunAgentDialog — ScribeFab mounts one app-wide and each SmartPackSelector mounts
	// another, none behind a v-if. Clearing does not cancel the request (nothing can); it stops a closed
	// dialog's work from disabling a live dialog's proceed button.
	const commitBarrier = provideAutocompleteCommitBarrier();
	onUnmounted(() => commitBarrier.clear());

	const proceedToNextStep = async () => {
		if (proceedingStep.value !== null) {
			// NOT a silent return any more. With the button state step-scoped, the action is clickable again
			// the moment the user goes Back, so this refusal is now reachable by a real gesture — and an
			// unexplained dead click is exactly what LSP-16816 was reported as.
			console.warn(
				`[run-agent-proceed] Refused: Reason=ProceedAlreadyInFlight, InFlightFor=${proceedingStep.value}, Now=${currentStep.value}`
			);
			return;
		}
		const stepAtEntry = currentStep.value;
		proceedingStep.value = stepAtEntry;
		proceedBlockedMessage.value = "";
		try {
			await proceedToNextStepInner();
		} finally {
			// Only release the slot we took. The guard above makes a second concurrent handler impossible
			// today; clearing unconditionally would still be wrong the day it is not, which is the same
			// mistake the barrier's own ownership checks exist to avoid.
			if (proceedingStep.value === stepAtEntry) {
				proceedingStep.value = null;
			}
		}
	};

	const proceedToNextStepInner = async () => {
		// A pasted autocomplete value can still be committing: blur fires before click, and resolving the
		// entry may need a lookup. Reading formValues before that settles is what made the first Run click
		// look dead while the value sat on screen (LSP-16816).
		// Snapshotted because the barrier can hold this handler for seconds and Back stays clickable
		// throughout — deliberately, so a slow proceed is escapable. Everything below reads currentStep,
		// so a user who backs out mid-drain was walked forward again a few seconds later.
		const stepAtProceed = currentStep.value;
		const committed = await commitBarrier.flush();
		if (currentStep.value !== stepAtProceed) {
			console.warn(`[run-agent-proceed] Refused: Reason=StepChangedDuringFlush, From=${stepAtProceed}, Now=${currentStep.value}`);
			return;
		}
		if (!committed) {
			// Two different refusals, and only one of them can be fixed by waiting. An abandoned commit
			// is removed only when it finally settles, so "try again" is advice that can never work
			// while the barrier is stuck — the dialog has to be reopened to release it.
			// Three refusals, three different things the user can do about them. An unresolved entry is
			// RECOVERABLE by fixing the field, so it must not be told to reopen the dialog; a stuck barrier
			// cannot be fixed by waiting, so it must not be told to wait.
			//
			// STUCK IS READ FIRST, matching flush()'s own precedence. flush() returns false on
			// `abandonedCommits.size > 0` before it ever evaluates anyUnresolved(), so when a renderer holds
			// both — a stuck commit on one field and an unresolved entry on another, which are independent
			// getters — the refusal it actually made was the stuck one. Reading them in the opposite order
			// handed the user advice that cannot clear a stuck barrier, and fixing the highlighted field just
			// produced the same message again, indefinitely.
			proceedBlockedMessage.value = commitBarrier.stuck
				? "We could not finish saving your last entry. Please close this dialog and open it again."
				: commitBarrier.hasUnresolvedEntries
					? "Please check the highlighted entries before continuing."
					: "We are still saving your last entry. Give it a moment and try again.";
			// The barrier gave up with a commit still outstanding, so the form cannot be trusted. Proceeding
			// is NOT safe here even though the validity check below would pass: when the user pastes over a
			// field that already holds a valid selection, handleAutocompleteSearch deliberately keeps the OLD
			// value in formValues while the new lookup runs — so validity reads true off a value the user has
			// already replaced on screen, and the run would submit the stale one.
			console.warn(`[run-agent-proceed] Refused: Reason=CommitBarrierTimedOut, Step=${currentStep.value}`);
			return;
		}
		// Rule: action buttons stay enabled even when the form is invalid; clicking just
		// does nothing until it's valid (no disabled state). See CLAUDE.md → Frontend.
		if (!canProceedFromCurrentStep.value) {
			if (currentStep.value === "review-requirements" && !allPlatformsAuthenticated.value) {
				proceedBlockedMessage.value = platformLoginGuidance;
			}
			// The rule above says the feedback is the fields' own red state — but Vuetify only paints a rule
			// once the field has been touched, so an untouched required field showed nothing and the click
			// looked dead (LSP-19461). Make the rules paint, then put the cursor where the problem is.
			const focused =
				currentStep.value === "provide-details" ? await revealInvalidUserInput(userInputForms, provideDetailsSection.value) : null;
			// Logged for the same reason as the shared twin: this is the terminal gate of the flow LSP-16816
			// is about ("Run does nothing"), so a future regression where the barrier drains cleanly but
			// validity still reads false must not be invisible on this copy while the other one logs.
			console.warn(
				`[run-agent-proceed] Refused: Reason=StepNotValid, Step=${currentStep.value}, AllUserInputValid=${allUserInputValid.value}, AllPlatformsAuthenticated=${allPlatformsAuthenticated.value}, Missing=${missingRequiredFieldLabels.value.join("|")}, Invalid=${invalidFieldLabels.value.join("|")}, FocusedInvalidField=${focused ? "yes" : "no"}`
			);
			return;
		}
		if (currentStep.value === "review-requirements") {
			if (hasUserInputItems.value) {
				currentStep.value = "provide-details";
			} else {
				await handleRunAnalysis();
			}
		} else if (currentStep.value === "provide-details") {
			await handleRunAnalysis();
		}
	};

	const handleRunAnalysis = async () => {
		if (props.mode === "collect") {
			emit("input-collected");
			emit("update:modelValue", false);
			return;
		}
		const success = await props.orchestrator.runAnalysis();
		if (success) {
			emit("analysis-created", "pending");
			emit("update:modelValue", false);
		}
	};

	const dialogTitle = computed(() => {
		if (currentStep.value === "review-requirements") return "Review Requirements";
		if (currentStep.value === "provide-details") return "Provide Details";
		return selectedPack.value?.name || "Run Report";
	});

	// When dialog opens, determine starting step based on orchestrator state
	watch(
		() => props.modelValue,
		(newValue) => {
			if (newValue) {
				// A refusal written by the PREVIOUS session must not greet this one. Closing mid-flush clears
				// this ref, and then the resumed flush writes the blocked copy back into a dialog nobody is
				// looking at — so the next open showed "still saving your last entry" over a barrier that
				// clear() had already emptied. Clearing on open is what the user actually experiences; the
				// late write itself is harmless once nothing can display it.
				proceedBlockedMessage.value = "";
				if (hasPlatformRequirements.value && !allPlatformsAuthenticated.value) {
					currentStep.value = "review-requirements";
				} else if (hasUserInputItems.value) {
					currentStep.value = "provide-details";
				}
			} else {
				showLogoutConfirm.value = false;
				pendingLogoutCodeName.value = null;
				// Closing is the real end of this dialog's work — it stays MOUNTED, only modelValue flips, so
				// onUnmounted does not fire here. Without this a hung lookup from a dismissed dialog still
				// gates the next open's first proceed.
				commitBarrier.clear();
				proceedBlockedMessage.value = "";
			}
		}
	);
</script>

<template>
	<!-- LSP-20159: not dismissable while a run is starting (in-flight, not validity) — closing calls
	     orchestrator.reset(), which clears the runningAnalysis flag that blocks a duplicate run. -->
	<v-dialog
		:model-value="modelValue"
		:persistent="runningAnalysis"
		@update:model-value="$emit('update:modelValue', $event)"
		max-width="780"
		:width="display.smAndDown.value ? '90%' : '80%'">
		<v-card class="pa-2 w-100" rounded="xl" elevation="8">
			<!-- Header -->
			<v-card-title class="d-flex align-center pa-4 pb-0">
				<div class="d-flex align-center">
					<v-btn icon variant="text" size="small" class="mr-2" :disabled="runningAnalysis" @click="goBack">
						<v-icon>mdi-arrow-left</v-icon>
					</v-btn>
					<span class="text-h5">{{ dialogTitle }}</span>
				</div>
				<v-spacer />
				<v-btn icon="mdi-close" variant="text" size="small" :disabled="runningAnalysis" @click="emit('update:modelValue', false)" />
			</v-card-title>

			<!-- Stepper indicator -->
			<div v-if="stepperSteps.length > 1" class="stepper-bar px-4 pt-3 pb-1">
				<div class="d-flex align-center ga-1">
					<template v-for="(step, idx) in stepperSteps" :key="step.key">
						<div
							class="stepper-step d-flex align-center ga-1 px-2 py-1 rounded"
							:class="{
								'stepper-step--active': step.key === currentStep,
								'stepper-step--completed': idx < currentStepIndex,
								'stepper-step--upcoming': idx > currentStepIndex,
								'stepper-step--clickable': idx < currentStepIndex
							}"
							@click="idx < currentStepIndex ? navigateToStep(step.key) : undefined">
							<v-icon size="16" :color="idx <= currentStepIndex ? 'primary' : 'grey-lighten-1'">
								{{ idx < currentStepIndex ? "mdi-check-circle" : step.icon }}
							</v-icon>
							<span class="text-caption font-weight-medium">{{ step.label }}</span>
						</div>
						<v-icon v-if="idx < stepperSteps.length - 1" size="14" color="grey-lighten-1" class="mx-0">
							mdi-chevron-right
						</v-icon>
					</template>
				</div>
			</div>

			<!-- Report name: pre-filled with the auto-generated default (ordinal-enhanced by the
			     orchestrator when this pack has run against this target before), editable. Shown
			     once regardless of which step(s) this dialog has, not nested inside either step's
			     content — a platform-auth-only pack never reaches "provide-details" at all. Only
			     "run" mode actually dispatches a report; "collect" mode just gathers input for a
			     scheduled pack, so there is no report being named yet. -->
			<v-card-text v-if="mode === 'run' && selectedPack" class="pa-4 pb-0">
				<v-text-field v-model="reportNameDraft" v-test="'report-name-input'" variant="outlined" density="compact" hide-details>
					<template #label><FieldLabel label="Report name (Optional)" /></template>
				</v-text-field>
			</v-card-text>

			<v-divider class="mt-2" />

			<!-- Content -->
			<v-card-text class="pa-4 dialog-content-area">
				<!-- LSP-16816: the commit barrier refused to vouch for the form. Without this the proceed
					 handler only console.warn'd and returned, so Run did nothing with no explanation —
					 which is the reported symptom, not a fix for it. -->
				<div v-if="proceedNotice" class="mb-4">
					<v-alert type="warning" variant="tonal" density="compact">
						{{ proceedNotice }}
					</v-alert>
				</div>
				<!-- Review Requirements (Platform Auth) -->
				<div v-if="currentStep === 'review-requirements' && selectedPack">
					<div v-if="error" class="mb-4">
						<v-alert type="error" variant="tonal">
							{{ error }}
						</v-alert>
					</div>

					<p class="text-body-2 text-medium-emphasis mb-4">
						{{
							platformRequirements.filter((p) => p.requiresAuth).length === 1
								? "The following platform is needed to run this report."
								: platformRequirements.filter((p) => p.requiresAuth).length > 1
									? `The following ${platformRequirements.filter((p) => p.requiresAuth).length} platforms are needed to run this report.`
									: "No additional platforms are needed to run this report."
						}}
					</p>

					<div class="d-flex flex-column ga-3">
						<div
							v-for="req in platformRequirements.filter((p) => p.requiresAuth)"
							:key="req.codeName"
							class="platform-req-card pa-4"
							:class="{
								'platform-req-card--authenticated': req.authStatus === 'Authenticated',
								'platform-req-card--unauthenticated': req.authStatus !== 'Authenticated'
							}">
							<!-- LSP-15976: stack the row on xs (<600px) so the platform name takes a
								full line and the orange "Not Logged In" chip + Login button sit
								together on the line below. Mirrors MarketingBanner.vue's responsive
								flex pattern — no orange-chip crowding next to a long name on phones. -->
							<div class="d-flex flex-column flex-sm-row align-start align-sm-center justify-space-between ga-2 mb-2">
								<div class="d-flex align-center ga-2">
									<span class="text-subtitle-1 font-weight-medium">{{ req.productName }}</span>
								</div>
								<div class="d-flex align-center ga-2">
									<v-chip :color="getPlatformAuthColor(req)" variant="tonal" size="small" label>
										<v-icon start size="x-small">{{ getPlatformAuthIcon(req) }}</v-icon>
										{{ getPlatformAuthLabel(req) }}
									</v-chip>
									<PrimaryBtn
										v-if="req.authStatus !== 'Authenticated'"
										size="small"
										:loading="platformBusy[req.codeName] === true"
										:disabled="platformBusy[req.codeName] === true"
										@click="loginPlatform(req.codeName)">
										{{ platformErrors?.[req.codeName] ? "Retry login" : "Login" }}
									</PrimaryBtn>
									<v-btn
										v-else
										size="small"
										variant="text"
										rounded="xl"
										:loading="platformBusy[req.codeName] === true"
										:disabled="platformBusy[req.codeName] === true"
										@click="requestLogout(req.codeName)">
										Logout
									</v-btn>
								</div>
							</div>
							<TertiaryBtn
								v-if="platformLoginPending?.[req.codeName]"
								size="small"
								@click="cancelPlatformLogin(req.codeName)">
								Cancel login
							</TertiaryBtn>
							<p v-if="platformErrors?.[req.codeName]" role="alert" class="text-error text-body-2 mt-2 mb-0">
								{{ platformErrors[req.codeName] }}
							</p>
						</div>
					</div>
				</div>

				<!-- Provide Details (User Input) -->
				<div v-else-if="currentStep === 'provide-details' && selectedPack">
					<div v-if="error" class="mb-4">
						<v-alert type="error" variant="tonal">
							{{ error }}
						</v-alert>
					</div>

					<div v-if="loadingUserInputRequirements" class="d-flex justify-center py-8">
						<v-progress-circular indeterminate color="primary" />
					</div>

					<div v-else-if="userInputRequirements.length === 0 && userInputTaskConfigs.length === 0" class="text-center py-8">
						<v-icon size="48" color="grey-lighten-1">mdi-form-textbox</v-icon>
						<div class="text-h6 mt-2">No Additional Input Required</div>
						<div class="text-body-2 text-medium-emphasis">You can proceed to run the report.</div>
					</div>

					<div v-else ref="provideDetailsSection" class="d-flex flex-column ga-4">
						<p class="text-body-2 text-medium-emphasis mb-2">The following information is needed to run the report.</p>

						<!-- Task inline form configs -->
						<div
							v-for="config in userInputTaskConfigs"
							:key="`task-${config.taskId}`"
							class="rounded-xl pa-4 bg-grey-lighten-4">
							<div class="d-flex align-center mb-3">
								<v-icon size="small" color="teal" class="mr-2">mdi-form-textbox</v-icon>
								<span class="text-subtitle-1 font-weight-medium">{{ config.taskName }}</span>
							</div>

							<p v-if="config.description" class="text-body-2 text-medium-emphasis mb-4">
								{{ config.description }}
							</p>

							<v-form :ref="trackUserInputForm" @submit.prevent>
								<UserInputFormRenderer
									:schema="config.inlineFormConfig"
									:requirement-key="`task_${config.taskId}`"
									:model-value="userInputData[`task_${config.taskId}`] || {}"
									:customer="pendingMetadata?.customer"
									@update:model-value="handleUserInputUpdate(`task_${config.taskId}`, $event)" />
							</v-form>
						</div>

						<!-- Knowledge requirement forms -->
						<div v-for="requirement in userInputRequirements" :key="requirement.id" class="rounded-xl pa-4 bg-grey-lighten-4">
							<div class="d-flex align-center mb-3">
								<v-icon size="small" color="primary" class="mr-2">mdi-form-textbox</v-icon>
								<span class="text-subtitle-1 font-weight-medium">{{ requirement.name }}</span>
								<v-chip v-if="requirement.isRequired" size="x-small" color="error" variant="tonal" class="ml-2">
									Required
								</v-chip>
							</div>

							<p v-if="requirement.description" class="text-body-2 text-medium-emphasis mb-4">
								{{ requirement.description }}
							</p>

							<v-form :ref="trackUserInputForm" @submit.prevent>
								<UserInputFormRenderer
									v-if="requirement.userInputSchema"
									:schema="requirement.userInputSchema"
									:requirement-key="requirement.requirementKey"
									:model-value="userInputData[requirement.requirementKey] || {}"
									:customer="pendingMetadata?.customer"
									@update:model-value="handleUserInputUpdate(requirement.requirementKey, $event)" />
							</v-form>
						</div>
					</div>
				</div>
			</v-card-text>

			<v-divider />

			<!-- Footer Actions -->
			<v-card-actions class="pa-4 pb-2 d-flex flex-column align-stretch ga-2">
				<!-- Status has its own wrapping row so validation cannot displace the actions. -->
				<!-- LSP-20159: the create request can take several seconds; say so instead of a bare spinner. -->
				<div v-if="runningAnalysis" class="text-body-2 text-medium-emphasis text-break" role="status">Starting your report…</div>
				<div v-else-if="currentStep === 'review-requirements'" class="d-flex align-start">
					<v-icon :color="allPlatformsAuthenticated ? 'success' : 'warning'" size="small" class="mr-1 flex-shrink-0">
						{{ allPlatformsAuthenticated ? "mdi-check-circle" : "mdi-alert-circle" }}
					</v-icon>
					<span class="text-body-2 text-break">
						{{ allPlatformsAuthenticated ? "All platforms connected" : "Platform login required" }}
					</span>
				</div>
				<div v-else-if="currentStep === 'provide-details'" class="d-flex align-start">
					<v-icon :color="allUserInputValid ? 'success' : 'warning'" size="small" class="mr-1 flex-shrink-0">
						{{ allUserInputValid ? "mdi-check-circle" : "mdi-alert-circle" }}
					</v-icon>
					<span class="text-body-2 text-break">
						{{ userInputStatusText }}
					</span>
				</div>
				<div class="d-flex justify-space-between align-center ga-2 w-100">
					<v-btn variant="text" rounded="xl" class="flex-shrink-0" @click="goBack" :disabled="runningAnalysis"> Back </v-btn>
					<PrimaryBtn
						class="flex-shrink-0"
						:disabled="runningAnalysis || proceeding"
						:loading="runningAnalysis || proceeding"
						@click="proceedToNextStep">
						<span v-if="!runningAnalysis">{{ buttonText }}</span>
					</PrimaryBtn>
				</div>
			</v-card-actions>
		</v-card>
	</v-dialog>

	<Dialog
		:dialog-visible="showLogoutConfirm"
		title="Disconnect Platform"
		:message="`Are you sure you want to log out of ${pendingLogoutPlatformName}? You'll need to log in again to run ${pendingLogoutPlatformName} reports.`"
		agree-text="Logout"
		cancel-text="Cancel"
		:is-angry="true"
		@agree="confirmLogout"
		@cancel="cancelLogout" />
</template>

<style scoped>
	.platform-req-card {
		border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
		border-radius: 8px;
		border-left: 3px solid rgba(var(--v-theme-on-surface), 0.12);
	}

	.platform-req-card--authenticated {
		border-left-color: rgb(var(--v-theme-success));
	}

	.platform-req-card--unauthenticated {
		border-left-color: rgb(var(--v-theme-warning));
	}

	.stepper-bar {
		background-color: rgba(var(--v-theme-on-surface), 0.02);
	}

	.stepper-step {
		transition: all 0.15s ease;
		user-select: none;
	}

	.stepper-step--active {
		background-color: rgba(var(--v-theme-primary), 0.08);
	}

	.stepper-step--completed {
		opacity: 0.7;
	}

	.stepper-step--upcoming {
		opacity: 0.4;
	}

	.stepper-step--clickable {
		cursor: pointer;
	}

	.stepper-step--clickable:hover {
		background-color: rgba(var(--v-theme-primary), 0.06);
		opacity: 1;
	}

	.dialog-content-area {
		min-height: 200px;
		max-height: 60vh;
		overflow-y: auto;
		position: relative;
	}
</style>
